// src/context/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import keepAliveService from '../services/keepAlive';

// ✅ PRODUCTION FIX: Use environment variable for Vercel deployment
const API_URL = "https://final-project-5ar7.onrender.com";

// ✅ ADD debug line after it:
console.log('🌐 AuthContext using API_URL:', API_URL);

// ✅ NETWORK FIX: Enhanced fetch with proper headers and error handling
const apiCall = async (endpoint, options = {}) => {
  const url = `${API_URL}${endpoint}`;
  console.log('🌐 API Call:', url);
  
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Access-Control-Allow-Origin': '*',
        ...options.headers
      },
      mode: 'cors',
      credentials: 'omit'
    });
    
    console.log('📡 Response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API Error:', errorText);
      throw new Error(errorText || `HTTP ${response.status}`);
    }
    
    return response;
  } catch (error) {
    console.error('❌ Network Error:', error);
    throw new Error(`Network Error: ${error.message}`);
  }
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // ✅ ANTI-REFRESH: Prevent unnecessary re-renders
  const userRef = useRef(null);
  const stableSessionRef = useRef(false);
  const sessionCheckIntervalRef = useRef(null);
  const isMountedRef = useRef(true);

  // ✅ ENHANCED: Restore session with stability check
  useEffect(() => {
    const token = localStorage.getItem("userToken");
    const userData = localStorage.getItem("userData");
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        
        // ✅ SECURITY: Check user approval status on session restore (ADMIN BYPASS)
        if (parsedUser.status !== 'approved' && !parsedUser.isAdmin && parsedUser.role !== 'admin') {
          console.warn('⚠️ AuthContext: Restoring session for non-approved user, clearing session');
          localStorage.removeItem("userToken");
          localStorage.removeItem("userData");
          setUser(null);
          userRef.current = null;
          stableSessionRef.current = true;
          setLoading(false);
          return;
        }
        
        // ✅ ANTI-REFRESH: Prevent unnecessary state updates
        if (!userRef.current || userRef.current.id !== parsedUser.id) {
          setUser(parsedUser);
          userRef.current = parsedUser;
          stableSessionRef.current = true;
          
          if (parsedUser.isAdmin || parsedUser.role === 'admin') {
            console.log('✅ AuthContext: Admin session restored:', parsedUser.email);
          } else {
            console.log('✅ AuthContext: Session restored for approved user:', parsedUser.email);
          }
        } else {
          // Session already matches, just mark as stable
          stableSessionRef.current = true;
        }
      } catch (error) {
        console.error('❌ AuthContext: Invalid session data, clearing...');
        localStorage.removeItem("userToken");
        localStorage.removeItem("userData");
        setUser(null);
        userRef.current = null;
        stableSessionRef.current = false;
      }
    } else {
      stableSessionRef.current = true; // No session, but stable state
    }
    
    setLoading(false);
  }, []); // ✅ Empty dependency array - runs once only

  // ✅ ANTI-REFRESH: Optional session validation (runs infrequently)
  useEffect(() => {
    if (!user || !stableSessionRef.current || !isMountedRef.current) {
      return;
    }

    // ✅ IMPORTANT: Only validate session every 30 minutes, not constantly!
    const validateSession = () => {
      if (!isMountedRef.current) return;
      
      const token = localStorage.getItem("userToken");
      const userData = localStorage.getItem("userData");
      
      if (!token || !userData) {
        console.warn('⚠️ AuthContext: Session lost, logging out');
        logout();
        return;
      }

      try {
        const parsedUser = JSON.parse(userData);
        
        // ✅ SECURITY: Check approval status during validation (ADMIN BYPASS)
        if (parsedUser.status !== 'approved' && !parsedUser.isAdmin && parsedUser.role !== 'admin') {
          console.warn('⚠️ AuthContext: User no longer approved, logging out');
          logout();
          return;
        }
        
        // Only update if user ID actually changed (prevents refresh loops)
        if (userRef.current && parsedUser.id !== userRef.current.id) {
          console.warn('⚠️ AuthContext: User ID mismatch, logging out');
          logout();
        }
      } catch (error) {
        console.error('❌ AuthContext: Session validation failed, logging out');
        logout();
      }
    };

    // ✅ CRITICAL: Long interval prevents constant refreshes
    sessionCheckIntervalRef.current = setInterval(validateSession, 30 * 60 * 1000); // 30 minutes

    // ✅ Cleanup interval on unmount
    return () => {
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
        sessionCheckIntervalRef.current = null;
      }
    };
  }, [user]); // ✅ Only re-run if user actually changes

  // ✅ KEEP-ALIVE INTEGRATION
  useEffect(() => {
    // ✅ Start keep-alive for ANY logged-in user (admin or regular user)
    if (user && !loading && stableSessionRef.current) {
      console.log('🚀 AuthContext: Starting keep-alive for user:', user.email);
      
      // ✅ Pass user info to keep-alive service
      keepAliveService.start({
        id: user.id,
        email: user.email,
        isAdmin: user.isAdmin || user.role === 'admin'
      });
    } else if (!user) {
      // ✅ Stop keep-alive when user logs out
      console.log('🛑 AuthContext: Stopping keep-alive - user logged out');
      keepAliveService.stop();
    }

    // ✅ Cleanup on unmount
    return () => {
      keepAliveService.stop();
    };
  }, [user, loading]); // ✅ React to user and loading changes

  // ✅ Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }
      keepAliveService.stop();
    };
  }, []);

  // ✅ FIXED REGISTER FUNCTION
  const register = async (formData) => {
    console.log('🚀 AuthContext: Starting registration...', formData.email);
    
    try {
      const response = await apiCall('/api/auth/register', {
        method: "POST",
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      console.log('✅ AuthContext: Registration successful - user pending approval');
      
      return data.user;
      
    } catch (error) {
      console.error('❌ AuthContext: Registration failed:', error);
      throw new Error(error.message || 'Registration failed. Please check your connection and try again.');
    }
  };

  // ✅ SECURITY ENHANCED: Login with approval status check (ADMIN BYPASS)
  const login = async (credentialsOrUser, tokenFromBackend) => {
    try {
      let userData, token;

      // 🔹 Attempt backend login if credentials provided
      if (!tokenFromBackend) {
        console.log('🔄 AuthContext: Attempting backend login...');
        
        const response = await apiCall('/api/auth/login', {
          method: "POST",
          body: JSON.stringify(credentialsOrUser),
        });

        const data = await response.json();
        userData = data.user;
        token = data.token;
        console.log('✅ AuthContext: Backend login successful');
      } else {
        // 🔹 Direct user data provided (from register or local)
        userData = credentialsOrUser;
        token = tokenFromBackend;
        console.log('✅ AuthContext: Direct login with provided data');
      }

      // ✅ SECURITY CHECK: Verify user approval status (ADMIN BYPASS)
      if (!userData.isAdmin && userData.role !== 'admin') {
        if (userData.status === 'pending') {
          throw new Error('🔒 Your account is pending admin approval. Please wait for approval before logging in.');
        }
        
        if (userData.status === 'rejected') {
          throw new Error('❌ Your account has been rejected. Please contact support.');
        }
        
        if (userData.status !== 'approved') {
          throw new Error('⚠️ Your account status is invalid. Please contact support.');
        }
      } else {
        console.log('🔑 AuthContext: Admin user - bypassing approval status checks');
      }

      // ✅ ANTI-REFRESH: Prevent unnecessary updates if user is already logged in
      if (userRef.current && userRef.current.id === userData.id) {
        console.log('✅ AuthContext: User already logged in, skipping duplicate login');
        return userRef.current;
      }

      // Store session
      localStorage.setItem("userToken", token);
      localStorage.setItem("userData", JSON.stringify(userData));
      
      // Update state with new user
      setUser(userData);
      userRef.current = userData;
      stableSessionRef.current = true;
      
      if (userData.isAdmin || userData.role === 'admin') {
        console.log('✅ AuthContext: Admin login successful:', userData.email);
      } else {
        console.log('✅ AuthContext: Login successful for approved user:', userData.email);
      }
      
      return userData;

    } catch (error) {
      console.error('❌ AuthContext: Login failed:', error);
      
      // 🔹 FALLBACK: Local fake login for development (preserving your behavior)
      if (!tokenFromBackend && typeof credentialsOrUser === 'object') {
        console.warn('⚠️ AuthContext: Using local fallback login...');
        
        // ✅ SECURITY CHECK: Even fake users need approval OR admin status
        if (!credentialsOrUser.isAdmin && credentialsOrUser.role !== 'admin') {
          if (credentialsOrUser.status === 'pending') {
            throw new Error('🔒 Account pending approval');
          }
          
          if (credentialsOrUser.status !== 'approved') {
            throw new Error('⚠️ Account not approved');
          }
        }
        
        const fakeToken = `fake-token-${Date.now()}`;
        const fakeUser = credentialsOrUser;
        
        localStorage.setItem("userToken", fakeToken);
        localStorage.setItem("userData", JSON.stringify(fakeUser));
        setUser(fakeUser);
        userRef.current = fakeUser;
        stableSessionRef.current = true;
        
        if (fakeUser.isAdmin || fakeUser.role === 'admin') {
          console.log('✅ AuthContext: Development admin fallback login');
        } else {
          console.log('✅ AuthContext: Development fallback login for approved user');
        }
        
        return fakeUser;
      }
      
      throw error;
    }
  };

  // ✅ ENHANCED: Logout with proper cleanup
  const logout = () => {
    const currentUser = userRef.current;
    console.log('🚪 AuthContext: Logging out user:', currentUser?.email || 'Unknown');
    
    // Clear local storage
    localStorage.removeItem("userToken");
    localStorage.removeItem("userData");
    
    // Clear session validation interval
    if (sessionCheckIntervalRef.current) {
      clearInterval(sessionCheckIntervalRef.current);
      sessionCheckIntervalRef.current = null;
    }
    
    // Stop keep-alive service
    keepAliveService.stop();
    
    // Reset state
    setUser(null);
    userRef.current = null;
    stableSessionRef.current = false;
    
    console.log('✅ AuthContext: Logout complete');
  };

  // ✅ ENHANCED: Update user with anti-refresh protection
  const updateUser = (newUserData) => {
    if (!userRef.current) {
      console.warn('⚠️ AuthContext: Cannot update user - no user logged in');
      return;
    }

    const updatedUser = { ...userRef.current, ...newUserData };
    
    // ✅ SECURITY: If user status changed to non-approved, logout (ADMIN BYPASS)
    if (updatedUser.status !== 'approved' && !updatedUser.isAdmin && updatedUser.role !== 'admin') {
      console.warn('⚠️ AuthContext: User status changed to non-approved, logging out');
      logout();
      return;
    }
    
    // ✅ ANTI-REFRESH: Only update if data actually changed
    const hasChanged = JSON.stringify(userRef.current) !== JSON.stringify(updatedUser);
    
    if (hasChanged) {
      localStorage.setItem("userData", JSON.stringify(updatedUser));
      setUser(updatedUser);
      userRef.current = updatedUser;
      console.log('✅ AuthContext: User updated:', updatedUser.email);
    } else {
      console.log('✅ AuthContext: User data unchanged, skipping update');
    }
  };

  // ✅ UTILITY: Check if user is admin
  const isAdmin = () => {
    return userRef.current && (userRef.current.isAdmin || userRef.current.role === 'admin');
  };

  // ✅ UTILITY: Get current token
  const getToken = () => {
    return localStorage.getItem("userToken");
  };

  const value = { 
    user, 
    login, 
    logout, 
    register, 
    updateUser, 
    loading,
    isSessionStable: stableSessionRef.current, // ✅ Session stability flag
    isAdmin: isAdmin(), // ✅ Admin check utility
    getToken // ✅ Token getter utility
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
